import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';

import BookingsConstants from '@/constants/BookingsConstants';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';

import Currencies from 'arbitrip-common/general/utils/Currencies';
import Dates from 'arbitrip-common/client/utils/Dates';
import { RESERVATION_STATUS } from 'arbitrip-common/client/constants/ReservationConstants';
import { LOYALTY_POINTS_PROVIDER } from 'arbitrip-common/general/constants';
import BookingsTableLoader from './BookingTableLoader.react';
import NoBookingsMessage from './NoBookingsMessage';
import NoBookingsYet from './NoBookingsYet';
import BookingStatus from '@/utils/BookingStatus';

import { getIsUnpaid } from '../../../utils/PayLater';
import Tooltip from '@mui/material/Tooltip';

import { ReactComponent as SortIcon } from '@/img/bookings/sort.svg';
import { ReactComponent as SortIconAsc } from '@/img/bookings/sort-asc.svg';
import { ReactComponent as SortIconDesc } from '@/img/bookings/sort-desc.svg';

import { LTR, RTL } from 'arbitrip-common/translation/const';
const { ORDER_BY, ORDER, ORDER_DEFAULTS, ORDER_OPPOSITES } = BookingsConstants;

const date_format = 'D MMM YYYY';

const border_spacing = {
    borderCollapse: 'separate',
    borderSpacing: '0 4px',
};

const StyledTablePagination = styled(TablePagination)(({ dir }) => ({
    '& .MuiTablePagination-displayedRows': {
        margin: dir === RTL ? '8px 0 0 0' : '0 0 3px 0',
    },
    '& .MuiToolbar-root': {
        padding: '0',
    },
}));

const StyledTableCell = styled(TableCell)(({ dir }) => ({
    textAlign: dir === RTL ? 'right' : 'left',
}));

function getTravelersText(booking, t) {
    let names_cap = 4;
    let children_text = '';
    if (booking.travelers.length >= 4) {
        names_cap--;
    }
    const childrenCount = (booking.deal.details.children_count || 0) * (booking.deal.details.room_count || 1)

	if (childrenCount>= 1) {
        names_cap--;
        children_text = `${childrenCount} ${childrenCount > 1 ? t('Bookings.Table.Children') : t('Bookings.Table.Child')}`;
    }

    let travelers_text = booking.travelers
        .map((t, idx) => (idx < names_cap ? `${t.first_name} ${t.last_name}` : null)).filter(t => t !== null)
        .join(', ');
    if (booking.travelers.length > names_cap) {
        const additional_adults = booking.travelers.length - names_cap;
        travelers_text += `, +${additional_adults} ${additional_adults > 1 ? t('Bookings.Table.Adults') : t('Bookings.Table.Adult')}`;
        if (children_text) {
            travelers_text += `, ${children_text}`;
        }
    } else if (children_text) {
        travelers_text += ` +${children_text}`;
    }

    return travelers_text;
}

function getCancellationDeadline(booking, i18n, t) {
    if (_.get(booking, 'deal.dca.nonRefundable') || !_.get(booking, 'deal.dca.dealIsFullyRefundableTill')) {
        return t('Bookings.Table.NonRefundableSpaced');
    }

    const deadline = _.get(booking, 'deal.dca.dealIsFullyRefundableTill');

    return Dates.formatCancellationDeadline(deadline, i18n.language);
}

function getCancellationDeadlineClass(booking) {
    if (_.get(booking, 'deal.dca.nonRefundable') || !_.get(booking, 'deal.dca.dealIsFullyRefundableTill')) {
        return '';
    }
    const deadlineMoment = moment.utc(_.get(booking, 'deal.dca.dealIsFullyRefundableTill'), 'YYYY-MM-DDTHH:mm:ss.sssZ');
    let updatedDeadlineMoment;
    if (!deadlineMoment.hours()) {
        updatedDeadlineMoment = moment
            .utc(_.get(booking, 'deal.dca.dealIsFullyRefundableTill'), 'YYYY-MM-DDTHH:mm:ss.sssZ')
            .subtract(1, 'd');
    } else {
        updatedDeadlineMoment = deadlineMoment;
    }

    const isFutureReservation = booking && moment.utc().isBefore(updatedDeadlineMoment);
    return isFutureReservation ? '' : 'deadline_exited';
}

function getBookingPriceDisplay(booking, t) {
    if (booking.payment?.loyalty?.points_provider === LOYALTY_POINTS_PROVIDER.ELAL) {
        const points = booking.payment?.loyalty?.points;
        const pointsInCurrency = booking.payment.loyalty.points_in_currency || 0;
        let paidPrice = booking.deal.cheapopoPricing.cheapopoTotalPrice - pointsInCurrency;
        if (paidPrice < 0) paidPrice = 0;

        const endPrice = Currencies.getPriceWithCurrency(paidPrice, booking.deal.cheapopoPricing.currency);

        return `${endPrice} + ${points.toLocaleString('en-US')} ${t('ShortPoints')}`;
    }

    return Currencies.getPriceWithCurrency(
        booking.deal.cheapopoPricing.cheapopoTotalPrice,
        booking.deal.cheapopoPricing.currency,
    );
}

const TruncatedTextTooltip = ({ children, title, placement = 'top' }) => {
    const textRef = React.useRef(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useEffect(() => {
        const checkTruncation = () => {
            if (textRef.current) {
                const element = textRef.current;
                setIsTruncated(element.scrollWidth > element.clientWidth);
            }
        };

        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [children]);

    if (isTruncated) {
        return (
            <Tooltip title={title || children} placement={placement}>
                <span ref={textRef} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {children}
                </span>
            </Tooltip>
        );
    }

    return (
        <span ref={textRef} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {children}
        </span>
    );
};

function CustomTablePaginationActions(props) {
    const { count, page, rowsPerPage, onPageChange, i18n } = props;
    const isRTL = i18n.dir() === RTL;

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    return (
        <div className="MuiTablePagination-actions" style={{ display: 'flex', gap: '8px' }}>
            <button
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label={isRTL ? 'Next page' : 'Previous page'}
                style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }}
            >
                <svg width="24" height="24" viewBox="0 0 24 24">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
            </button>
            <button
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label={isRTL ? 'Previous page' : 'Next page'}
                style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }}
            >
                <svg width="24" height="24" viewBox="0 0 24 24">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
            </button>
        </div>
    );
}

class BookingsTable extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            rows_per_page_options: [
                // 5,
                // 10,
                // 25,
                // ...(Config.dev_mode ? [{ label: 'All', value: -1 }] : [])
                BookingsConstants.BOOKINGS_LIMIT,
            ],
        };

        // // Avoid a layout jump when reaching the last page with empty rows.
        // this.state.empty_rows = (this.state.page > 0)
        //     ? Math.max(0, (1 + this.state.page) * this.state.rows_per_page - this.state.rows.length)
        //     : 0;

        this.handleChangePage = this.handleChangePage.bind(this);
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
        this.createSortHandler = this.createSortHandler.bind(this);
        this.onRequestSort = this.onRequestSort.bind(this);
        this.getSortIcon = this.getSortIcon.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps?.points?.balance !== this.props?.points?.balance) {
            if (this.props.selectedBooking) {
                this.props.updateDrawer(this.props.selectedBooking);
            }
        }
    }

    getSortIcon(orderBy) {
        if (this.props.sort.order_by === orderBy) {
            switch (this.props.sort.order) {
                case ORDER.ASC:
                    return <SortIconAsc />;

                case ORDER.DESC:
                    return <SortIconDesc />;

                default:
                    return <SortIcon />;
            }
        }

        return <SortIcon />;
    }

    areFiltersActive() {
        const { filters } = this.props;
        return _.some(filters, (value) => !_.isEmpty(value));
    }

    handleChangePage(event, newPage) {
        console.log('handleChangePage', event.target.value, newPage);
        this.props.setPagination({
            ...this.props.pagination,
            page: newPage,
        });
    }

    handleChangeRowsPerPage(event, newRowsPerPage) {
        console.log('handleChangeRowsPerPage', event.target.value, newRowsPerPage);
        this.props.setPagination({
            ...this.props.pagination,
            rows_per_page: parseInt(event.target.value, 10),
        });
    }

    createSortHandler(property) {
        const _self = this;
        return (event) => {
            _self.onRequestSort(event, property);
        };
    }

    onRequestSort(event, property) {
        const { order_by, order } = this.props.sort;
        const new_order_by = property;

        const default_order = ORDER_DEFAULTS[new_order_by];

        const new_order =
            order_by !== new_order_by
                ? default_order
                : order !== default_order
                  ? default_order
                  : ORDER_OPPOSITES[default_order];

        this.props.setSort({
            order_by: new_order_by,
            order: new_order,
        });
    }

    renderNoBookingsMessage() {
        const { t } = this.props;
        const { status, bookings, displayTabs } = this.props;

        if (status !== BookingsConstants.STATUS.SUCCESS || bookings.length !== 0) {
            return null;
        }

        if (this.areFiltersActive()) {
            return <NoBookingsMessage iconType="filter" message={t('NoResults')} subMessage="" />;
        }

        if (displayTabs) {
            return (
                <NoBookingsMessage
                    iconType="default"
                    message={t('NoBookingsUnderTab')}
                    subMessage={t('ExploreOtherTabs')}
                />
            );
        }

        return <NoBookingsYet />;
    }

    render() {
        const {
            profile,
            bookings,
            bookingsCount,
            selectedBooking,
            openDrawer,
            status,
            bookingsCountStatus,
            pagination,
            sort,
            showLoader,
            i18n,
            t,
        } = this.props;

        const { rows_per_page_options } = this.state;
        const { page, rows_per_page } = pagination;

        const { order_by, order } = sort;

        const _self = this;
        const isRTL = i18n.dir() === RTL;

        return (
            <div className="bookings-table-component">
                {(status === BookingsConstants.STATUS.BUSY || showLoader) && (
                    <BookingsTableLoader profile={profile} classes={{ border_spacing }} />
                )}

                {bookings.length > 0 && status !== BookingsConstants.STATUS.BUSY && (
                    <TableContainer component={Paper} className="bookings-table-container">
                        <Table className="bookings-table" aria-label="bookings table" sx={{ ...border_spacing }}>
                            <TableHead>
                                <TableRow>
                                    {profile.agent && (
                                        <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__client">
                                            {t('Bookings.Table.Client')}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__hotel">
                                        {t('Bookings.Table.Hotel')}
                                    </StyledTableCell>
                                    <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__city">
                                        {t('Bookings.Table.City')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        dir={isRTL ? RTL : LTR}
                                        className="cell__date check-in"
                                        //  key={headCell.id}
                                        //  align={headCell.numeric ? 'right' : 'left'}
                                        align="left"
                                        //  padding={headCell.disablePadding ? 'none' : 'normal'}
                                        sortDirection={order_by === ORDER_BY.CHECK_IN ? order : false}
                                    >
                                        <TableSortLabel
                                            active={order_by === ORDER_BY.CHECK_IN}
                                            direction={order_by === ORDER_BY.CHECK_IN ? order : ORDER.ASC}
                                            onClick={this.createSortHandler(ORDER_BY.CHECK_IN)}
                                            IconComponent={() => _self.getSortIcon(ORDER_BY.CHECK_IN)}
                                            aria-label={`Check-in column, currently ${order_by === ORDER_BY.CHECK_IN ? `sorted in ${order === 'asc' ? 'ascending' : 'descending'} order` : 'not sorted. Click to sort'}`}
                                        >
                                            {t('Bookings.Table.CheckIn')}
                                            {/* {orderBy === headCell.id ? (
                                            <Box component="span" sx={visuallyHidden}>
                                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                            </Box>
                                        ) : null} */}
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell
                                        dir={isRTL ? RTL : LTR}
                                        className="cell__date check-out"
                                        align="left"
                                        sortDirection={order_by === ORDER_BY.CHECK_OUT ? order : false}
                                    >
                                        <TableSortLabel
                                            active={order_by === ORDER_BY.CHECK_OUT}
                                            direction={
                                                order_by === ORDER_BY.CHECK_OUT
                                                    ? order
                                                    : ORDER_DEFAULTS[ORDER_BY.CHECK_OUT]
                                            }
                                            onClick={this.createSortHandler(ORDER_BY.CHECK_OUT)}
                                            IconComponent={() => _self.getSortIcon(ORDER_BY.CHECK_OUT)}
                                            aria-label={`Check-out column, ${order_by === ORDER_BY.CHECK_OUT ? `sorted in ${order === 'asc' ? 'ascending' : 'descending'} order` : 'not sorted. Click to sort'}`}
                                        >
                                            {t('Bookings.Table.CheckOut')}
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__travelers">
                                        {t('Bookings.Table.Travelers')}
                                    </StyledTableCell>
                                    <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__id">
                                        {t('Bookings.Table.ReservationID')}
                                    </StyledTableCell>
                                    <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__cancellation">
                                        <TableSortLabel
                                            active={order_by === ORDER_BY.CANCELLATION_DEADLINE}
                                            direction={
                                                order_by === ORDER_BY.CANCELLATION_DEADLINE
                                                    ? order
                                                    : ORDER_DEFAULTS[ORDER_BY.CANCELLATION_DEADLINE]
                                            }
                                            onClick={this.createSortHandler(ORDER_BY.CANCELLATION_DEADLINE)}
                                            IconComponent={() => this.getSortIcon(ORDER_BY.CANCELLATION_DEADLINE)}
                                            aria-label={`Cancellation deadline column, ${order_by === ORDER_BY.CANCELLATION_DEADLINE ? `sorted in ${order === 'asc' ? 'ascending' : 'descending'} order` : 'not sorted. Click to sort'}`}
                                        >
                                            {t('Bookings.Table.CancellationBefore')}
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__price">
                                        {t('Bookings.Table.TotalPrice')}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        dir={isRTL ? RTL : LTR}
                                        className="booking-status"
                                        align="left"
                                        sortDirection={order_by === ORDER_BY.STATUS ? order : false}
                                    >
                                        <TableSortLabel
                                            active={order_by === ORDER_BY.STATUS}
                                            direction={
                                                order_by === ORDER_BY.STATUS ? order : ORDER_DEFAULTS[ORDER_BY.STATUS]
                                            }
                                            onClick={this.createSortHandler(ORDER_BY.STATUS)}
                                            IconComponent={() => _self.getSortIcon(ORDER_BY.STATUS)}
                                            aria-label={`Status column, ${order_by === ORDER_BY.STATUS ? `sorted in ${order === 'asc' ? 'ascending' : 'descending'} order` : 'not sorted. Click to sort'}`}
                                        >
                                            {t('Bookings.Table.Status')}
                                        </TableSortLabel>
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bookings.map((booking) => {
                                    const travelers = getTravelersText(booking, t);
                                    const isApproved = booking.status === RESERVATION_STATUS.APPROVED;
                                    const booking_status = BookingStatus.getBookingStatus(booking.status, t);

                                    return (
                                        <TableRow
                                            key={booking.id}
                                            onClick={() => openDrawer(booking)}
                                            selected={selectedBooking && selectedBooking.id === booking.id}
                                            className="booking-row"
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`View more details about booking ${booking.short_id} to ${booking.hotel.name}, ${booking.hotel.city}`}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    openDrawer(booking);
                                                }
                                            }}
                                            data-testid={isApproved ? 'approved-booking-item' : undefined}
                                        >
                                            {profile.agent && (
                                                <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__client">
                                                    <Tooltip title={booking.company.name}>
                                                        <div className="booking-client-logo-container">
                                                            <img
                                                                src={booking.company.picture}
                                                                className="booking-client-logo"
                                                                alt={booking.company.name}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                </StyledTableCell>
                                            )}
                                            <StyledTableCell dir="ltr" className="cell__hotel">
                                                <TruncatedTextTooltip title={booking.hotel.name}>
                                                    {booking.hotel.name}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell dir="ltr" className="cell__city">
                                                <TruncatedTextTooltip title={booking.hotel.city}>
                                                    {booking.hotel.city}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__date">
                                                <TruncatedTextTooltip title={moment(booking.deal.details.check_in).format(date_format)}>
                                                    {moment(booking.deal.details.check_in).format(date_format)}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__date">
                                                <TruncatedTextTooltip title={moment(booking.deal.details.check_out).format(date_format)}>
                                                    {moment(booking.deal.details.check_out).format(date_format)}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell dir="ltr" className="cell__travelers">
                                                <TruncatedTextTooltip title={travelers}>
                                                    {travelers}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__id">
                                                <TruncatedTextTooltip title={booking.short_id}>
                                                    {booking.short_id}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell
                                                dir={isRTL ? RTL : LTR}
                                                className={`cell__cancellation ${getCancellationDeadlineClass(booking)}`}
                                            >
                                                <TruncatedTextTooltip title={getCancellationDeadline(booking, i18n, t)}>
                                                    {getCancellationDeadline(booking, i18n, t)}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell dir={isRTL ? RTL : LTR} className="cell__price">
                                                <TruncatedTextTooltip title={getBookingPriceDisplay(booking, t)}>
                                                    {getBookingPriceDisplay(booking, t)}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                            <StyledTableCell
                                                dir={isRTL ? RTL : LTR}
                                                className={`booking-status ${getIsUnpaid(profile, booking) ? 'unpaid' : booking.status}`}
                                            >
                                                <TruncatedTextTooltip
                                                    title={
                                                        getIsUnpaid(profile, booking)
                                                            ? `● ${booking_status}\n${t('Unpaid')}`
                                                            : `● ${booking_status}`
                                                    }
                                                >
                                                    ● {booking_status}
                                                    <br />
                                                    {getIsUnpaid(profile, booking) && (
                                                        <span className="booking-status pending indent">{t('Unpaid')}</span>
                                                    )}
                                                </TruncatedTextTooltip>
                                            </StyledTableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <StyledTablePagination
                                        rowsPerPageOptions={rows_per_page_options}
                                        labelDisplayedRows={({ from, to, count }) =>
                                            bookingsCountStatus === BookingsConstants.COUNT_STATUS.BUSY ? (
                                                <i className="fa fa-spin fa-spinner" />
                                            ) : (
                                                `${from}–${to} ${t('Of')} ${count}${count % 100 ? '' : '+'}`
                                            )
                                        }
                                        count={bookingsCount}
                                        rowsPerPage={rows_per_page}
                                        page={page}
                                        SelectProps={{
                                            inputProps: {
                                                'aria-label': 'rows per page',
                                            },
                                            native: true,
                                        }}
                                        onPageChange={this.handleChangePage}
                                        ActionsComponent={(props) => (
                                            <CustomTablePaginationActions {...props} i18n={i18n} />
                                        )}
                                        showFirstButton={false}
                                        showLastButton={false}
                                        dir={i18n.dir()}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                )}

                {this.renderNoBookingsMessage()}
            </div>
        );
    }
}

export default withTranslation()(BookingsTable);
